import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { CleanTime, CleanTimePayload } from 'Interfaces/ReferenceInterfaces/CleanTimeInterface';
import { DataTableStateEvent } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';

interface FormData {
  roomId: number;
  coatingId: number | null; // Allow null for N/A
  cleanTypeId: number;
  outgoingJobTypeId: number;
  cleaningTimeMinutes: number;
}

export const useCleanTimeTable = (pageSize: number = 5) => {
  const { cleanTimeStore, roomStore, coatingTypeStore, cleanTypeStore, runTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<CleanTime | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    cleanTimeStore.fetchCleanTimes(currentPage, pageSize);
    roomStore.fetchRooms();
    roomStore.fetchRoomTypes();
    coatingTypeStore.fetchCoatingTypes();
    cleanTypeStore.fetchCleanTypes();
    runTypeStore.fetchRunTypes();
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await cleanTimeStore.pollCleanTimes(currentPage, pageSize);
      await roomStore.pollRoomTypes();
    } catch (error) {
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Clean Time data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1;
      setCurrentPage(newPage);
      cleanTimeStore.fetchCleanTimes(newPage, 5);
    }
  };

  // Prepare room options from roomStore room types
  const roomOptions = roomStore.roomTypeOptions;

  // Prepare coating options from coatingTypeStore
  const coatingOptions = [
    { label: 'N/A', value: null }, // Add N/A option
    ...coatingTypeStore.coatingTypes.map(coatingType => ({
      label: coatingType.description || '',
      value: coatingType.id
    }))
  ];

  // Prepare clean type options from cleanTypeStore
  const cleanTypeOptions = cleanTypeStore.cleanTypes.map(cleanType => ({
    label: cleanType.description || '',
    value: cleanType.id
  }));

  // Prepare outgoing job type options from runTypeStore
  const outgoingJobTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      const updatePayload: CleanTimePayload = {
        Room_Type: formData.roomId,
        coating: formData.coatingId === null ? null : formData.coatingId,
        CleanType: formData.cleanTypeId,
        outgoing_job_type: formData.outgoingJobTypeId,
        cleaning_time_minutes: formData.cleaningTimeMinutes
      };

      const success = await cleanTimeStore.updateCleanTime(selectedRow.id, updatePayload);
      if (!success) {
        throw new Error('Failed to update clean time');
      }
    } else {
      const createPayload: CleanTimePayload = {
        Room_Type: formData.roomId,
        coating: formData.coatingId === null ? null : formData.coatingId,
        CleanType: formData.cleanTypeId,
        outgoing_job_type: formData.outgoingJobTypeId,
        cleaning_time_minutes: formData.cleaningTimeMinutes,
        flag: true
      };

      const success = await cleanTimeStore.createCleanTime(createPayload);
      if (!success) {
        throw new Error('Failed to create clean time');
      }
    }
    // Store methods already fetch updated data - no need to fetch again
    // Don't clear selectedRow here - let the modal's onHide handle cleanup
  };

  const handleDeleteRow = async () => {
    if (selectedRow) {
      await cleanTimeStore.deleteCleanTime(selectedRow.id);
      setSelectedRow(null);
      setShowDeleteModal(false);
      await cleanTimeStore.fetchCleanTimes(currentPage, pageSize);
    }
  };

  const prepareFormData = (row: CleanTime): FormData => {
    return {
      roomId: row.Room_Type.id || 0,
      coatingId: row.coating?.id || null, // Handle null coating
      cleanTypeId: row.CleanType.id || 0,
      outgoingJobTypeId: row.outgoing_job_type.id || 0,
      cleaningTimeMinutes: row.cleaning_time_minutes || 0
    };
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filteredCleanTimeData = cleanTimeStore.cleanTimes.filter(item => {
    if (!searchQuery.trim()) {
      return true;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      item.Room_Type.description.toLowerCase().includes(searchLower) ||
      item.coating?.description?.toLowerCase().includes(searchLower) ||
      item.CleanType.description?.toLowerCase().includes(searchLower) ||
      item.outgoing_job_type.description?.toLowerCase().includes(searchLower) ||
      item.cleaning_time_minutes.toString().includes(searchQuery)
    );
  });

  const handleExportToExcel = () => {
    const exportData = filteredCleanTimeData.map(item => ({
      'Room Type': item.Room_Type.description,
      Coating: item.coating?.description || 'N/A',
      'Clean Type': item.CleanType.description,
      'Outgoing Job Type': item.outgoing_job_type.description,
      'Clean Time (mins)': item.cleaning_time_minutes
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clean Times');
    XLSX.writeFile(workbook, 'clean_times_export.xlsx');
  };

  return {
    cleanTimeData: filteredCleanTimeData,
    isLoading: cleanTimeStore.isLoading || roomStore.roomTypesLoading,
    pagination: cleanTimeStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    coatingOptions,
    roomOptions,
    cleanTypeOptions,
    outgoingJobTypeOptions,
    onPageChange,
    currentPage,
    searchQuery,
    handleSearchChange,
    handleExportToExcel
  };
};
